import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { 
  Users, AlertTriangle, Clock, Activity, Shield, 
  CheckCircle2, Eye, RefreshCw, Plus, User, Plane,
  Calendar, Moon, Sun, Battery, BatteryLow, BatteryWarning,
  FileText, AlertCircle, TrendingUp, Settings, ChevronRight,
  Timer, BedDouble, MapPin, MoreVertical, Zap, Gauge,
  CalendarDays, Grid3X3, List, ArrowRight, X, GripVertical, Mic
} from 'lucide-react';
import VoiceIncidentRecorder from '@/components/VoiceIncidentRecorder';
import AIFieldSuggestions from '@/components/AIFieldSuggestions';
import PolishButton from '@/components/PolishButton';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import DragDropScheduler from '@/components/scheduling/DragDropScheduler';

const API_URL = import.meta.env.VITE_API_URL || process.env.REACT_APP_BACKEND_URL;

// Risk level colors and badges
const riskLevelConfig = {
  low: { color: 'bg-green-500', badge: 'bg-green-100 text-green-800', label: 'Low', icon: Battery },
  moderate: { color: 'bg-yellow-500', badge: 'bg-yellow-100 text-yellow-800', label: 'Moderate', icon: Battery },
  high: { color: 'bg-orange-500', badge: 'bg-orange-100 text-orange-800', label: 'High', icon: BatteryLow },
  critical: { color: 'bg-red-500', badge: 'bg-red-100 text-red-800', label: 'Critical', icon: BatteryWarning },
  unacceptable: { color: 'bg-rose-700', badge: 'bg-rose-100 text-rose-800', label: 'Unacceptable', icon: AlertTriangle },
};

// Severity config for conflicts
const severityConfig = {
  critical: { color: 'bg-red-500', badge: 'bg-red-100 text-red-800' },
  high: { color: 'bg-orange-500', badge: 'bg-orange-100 text-orange-800' },
  moderate: { color: 'bg-yellow-500', badge: 'bg-yellow-100 text-yellow-800' },
  low: { color: 'bg-green-500', badge: 'bg-green-100 text-green-800' },
};

// Crew role display names
const roleNames = {
  captain: 'Captain',
  first_officer: 'First Officer',
  second_officer: 'Second Officer',
  relief_pilot: 'Relief Pilot',
  flight_engineer: 'Flight Engineer',
  cabin_senior: 'Cabin Senior',
  cabin_crew: 'Cabin Crew',
  loadmaster: 'Loadmaster',
  dispatcher: 'Dispatcher',
};

// Duty type display names
const dutyTypeNames = {
  flight_duty: 'Flight Duty',
  positioning: 'Positioning',
  standby_airport: 'Airport Standby',
  standby_home: 'Home Standby',
  reserve: 'Reserve',
  training: 'Training',
  simulator: 'Simulator',
  ground_duty: 'Ground Duty',
  administrative: 'Administrative',
  rest: 'Rest',
  off_duty: 'Off Duty',
};

// Framework display names
const frameworkNames = {
  icao: 'ICAO Annex 6',
  faa_117: 'FAA Part 117',
  easa_ftl: 'EASA FTL',
  gaca: 'GACA',
  custom: 'Custom',
};

export default function FRMSPage() {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(false);
  
  // Dashboard data
  const [stats, setStats] = useState(null);
  
  // Crew data
  const [crew, setCrew] = useState([]);
  const [selectedCrew, setSelectedCrew] = useState(null);
  
  // Duties data
  const [duties, setDuties] = useState([]);
  const [selectedDuty, setSelectedDuty] = useState(null);
  
  // Alerts data
  const [alerts, setAlerts] = useState([]);
  
  // Reports data
  const [reports, setReports] = useState([]);
  
  // Regulatory limits
  const [limits, setLimits] = useState([]);
  
  // Scheduling data
  const [rosters, setRosters] = useState([]);
  const [selectedRoster, setSelectedRoster] = useState(null);
  const [rosterEntries, setRosterEntries] = useState([]);
  const [rosterHeatmap, setRosterHeatmap] = useState([]);
  const [rosterConflicts, setRosterConflicts] = useState([]);
  const [rosterPredictions, setRosterPredictions] = useState([]);
  const [schedulingView, setSchedulingView] = useState('drag-drop'); // 'drag-drop' | 'calendar' | 'list'
  
  // Reference data
  const [crewRoles, setCrewRoles] = useState([]);
  const [dutyTypes, setDutyTypes] = useState([]);
  const [frameworks, setFrameworks] = useState([]);
  const [samnPerelli, setSamnPerelli] = useState([]);
  const [contributingFactors, setContributingFactors] = useState([]);
  
  // Dialogs
  const [showCrewDialog, setShowCrewDialog] = useState(false);
  const [showCrewDetail, setShowCrewDetail] = useState(false);
  const [showDutyDialog, setShowDutyDialog] = useState(false);
  const [showReportDialog, setShowReportDialog] = useState(false);
  // Voice-to-Fatigue-Report
  const [voiceOpen, setVoiceOpen] = useState(false);
  const [showFatigueAssessment, setShowFatigueAssessment] = useState(false);
  
  // Assessment result
  const [assessmentResult, setAssessmentResult] = useState(null);
  
  // New crew form
  const [newCrew, setNewCrew] = useState({
    employee_id: '',
    name: '',
    name_ar: '',
    role: 'captain',
    license_number: '',
    home_base: 'OERK',
    home_timezone: 'Asia/Riyadh',
    aircraft_types: [],
    regulatory_framework: 'gaca',
  });
  
  // New duty form
  const [newDuty, setNewDuty] = useState({
    crew_member_id: '',
    duty_type: 'flight_duty',
    report_time: '',
    release_time: '',
    report_location: 'OERK',
    release_location: '',
    sectors: 1,
    total_flight_time: 0,
    augmented_crew: false,
    total_crew_count: 2,
  });
  
  // New report form
  const [newReport, setNewReport] = useState({
    crew_member_id: '',
    report_type: 'fatigue_event',
    title: '',
    description: '',
    event_date: new Date().toISOString().split('T')[0],
    hours_awake: null,
    hours_on_duty: null,
    sleep_in_last_24h: null,
    factors: [],
    safety_impact: 'none',
    anonymous: false,
  });

  const apiHeaders = {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };

  // Fetch dashboard stats
  const fetchStats = async () => {
    try {
      const res = await fetch(`${API_URL}/api/frms/dashboard`, { headers: apiHeaders });
      if (res.ok) {
        const data = await res.json();
        setStats(data);
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  // Fetch crew members
  const fetchCrew = async () => {
    try {
      const res = await fetch(`${API_URL}/api/frms/crew`, { headers: apiHeaders });
      if (res.ok) {
        const data = await res.json();
        setCrew(data.crew || []);
      }
    } catch (err) {
      console.error('Error fetching crew:', err);
    }
  };

  // Fetch duties
  const fetchDuties = async () => {
    try {
      const res = await fetch(`${API_URL}/api/frms/duties?limit=50`, { headers: apiHeaders });
      if (res.ok) {
        const data = await res.json();
        setDuties(data.duties || []);
      }
    } catch (err) {
      console.error('Error fetching duties:', err);
    }
  };

  // Fetch alerts
  const fetchAlerts = async () => {
    try {
      const res = await fetch(`${API_URL}/api/frms/alerts?resolved=false`, { headers: apiHeaders });
      if (res.ok) {
        const data = await res.json();
        setAlerts(data.alerts || []);
      }
    } catch (err) {
      console.error('Error fetching alerts:', err);
    }
  };

  // Fetch reports
  const fetchReports = async () => {
    try {
      const res = await fetch(`${API_URL}/api/frms/reports`, { headers: apiHeaders });
      if (res.ok) {
        const data = await res.json();
        setReports(data.reports || []);
      }
    } catch (err) {
      console.error('Error fetching reports:', err);
    }
  };

  // Fetch regulatory limits
  const fetchLimits = async () => {
    try {
      const res = await fetch(`${API_URL}/api/frms/limits`, { headers: apiHeaders });
      if (res.ok) {
        const data = await res.json();
        setLimits(data.limits || []);
      }
    } catch (err) {
      console.error('Error fetching limits:', err);
    }
  };

  // Fetch reference data
  const fetchReferenceData = async () => {
    try {
      const [rolesRes, typesRes, fwRes, spRes, factorsRes] = await Promise.all([
        fetch(`${API_URL}/api/frms/reference/roles`, { headers: apiHeaders }),
        fetch(`${API_URL}/api/frms/reference/duty-types`, { headers: apiHeaders }),
        fetch(`${API_URL}/api/frms/reference/frameworks`, { headers: apiHeaders }),
        fetch(`${API_URL}/api/frms/reference/samn-perelli`, { headers: apiHeaders }),
        fetch(`${API_URL}/api/frms/reference/factors`, { headers: apiHeaders }),
      ]);
      
      if (rolesRes.ok) {
        const data = await rolesRes.json();
        setCrewRoles(data.roles || []);
      }
      if (typesRes.ok) {
        const data = await typesRes.json();
        setDutyTypes(data.duty_types || []);
      }
      if (fwRes.ok) {
        const data = await fwRes.json();
        setFrameworks(data.frameworks || []);
      }
      if (spRes.ok) {
        const data = await spRes.json();
        setSamnPerelli(data.scale || []);
      }
      if (factorsRes.ok) {
        const data = await factorsRes.json();
        setContributingFactors(data.factors || []);
      }
    } catch (err) {
      console.error('Error fetching reference data:', err);
    }
  };

  // Fetch rosters for scheduling
  const fetchRosters = async () => {
    try {
      const res = await fetch(`${API_URL}/api/frms/scheduling/rosters`, { headers: apiHeaders });
      if (res.ok) {
        const data = await res.json();
        setRosters(data.rosters || []);
        // Auto-select the first roster if available
        if (data.rosters?.length > 0 && !selectedRoster) {
          await selectRoster(data.rosters[0]);
        }
      }
    } catch (err) {
      console.error('Error fetching rosters:', err);
    }
  };

  // Select a roster and load its data
  const selectRoster = async (roster) => {
    setSelectedRoster(roster);
    try {
      const [entriesRes, heatmapRes, conflictsRes, predictionsRes] = await Promise.all([
        fetch(`${API_URL}/api/frms/scheduling/rosters/${roster.id}/entries`, { headers: apiHeaders }),
        fetch(`${API_URL}/api/frms/scheduling/rosters/${roster.id}/heatmap`, { headers: apiHeaders }),
        fetch(`${API_URL}/api/frms/scheduling/rosters/${roster.id}/conflicts`, { headers: apiHeaders }),
        fetch(`${API_URL}/api/frms/scheduling/rosters/${roster.id}/predictions`, { headers: apiHeaders }),
      ]);
      
      if (entriesRes.ok) {
        const data = await entriesRes.json();
        setRosterEntries(data.entries || []);
      }
      if (heatmapRes.ok) {
        const data = await heatmapRes.json();
        setRosterHeatmap(data.heatmap || []);
      }
      if (conflictsRes.ok) {
        const data = await conflictsRes.json();
        setRosterConflicts(data.conflicts || []);
      }
      if (predictionsRes.ok) {
        const data = await predictionsRes.json();
        setRosterPredictions(data.predictions || []);
      }
    } catch (err) {
      console.error('Error loading roster data:', err);
    }
  };

  // Analyze roster
  const analyzeRoster = async (rosterId) => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/frms/scheduling/rosters/${rosterId}/analyze`, {
        method: 'POST',
        headers: apiHeaders
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Analysis complete: ${data.predictions_created} predictions, ${data.conflicts_detected} conflicts`);
        await selectRoster(selectedRoster);
        await fetchRosters();
      } else {
        toast.error('Failed to analyze roster');
      }
    } catch (err) {
      toast.error('Error analyzing roster');
    } finally {
      setLoading(false);
    }
  };

  // Create demo roster
  const seedDemoRoster = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/frms/scheduling/seed-demo`, {
        method: 'POST',
        headers: apiHeaders
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        await fetchRosters();
      } else {
        toast.error('Failed to create demo roster');
      }
    } catch (err) {
      toast.error('Error creating demo roster');
    } finally {
      setLoading(false);
    }
  };

  // Seed demo data
  const seedDemoData = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/frms/seed`, {
        method: 'POST',
        headers: apiHeaders
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        await fetchAll();
      } else {
        toast.error('Failed to seed demo data');
      }
    } catch (err) {
      toast.error('Error seeding demo data');
    } finally {
      setLoading(false);
    }
  };

  // Calculate fatigue assessment
  const assessFatigue = async (crewId) => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/frms/crew/${crewId}/fatigue`, {
        method: 'GET',
        headers: apiHeaders
      });
      if (res.ok) {
        const data = await res.json();
        setAssessmentResult(data);
        setShowFatigueAssessment(true);
      } else {
        toast.error('Failed to calculate fatigue assessment');
      }
    } catch (err) {
      toast.error('Error calculating fatigue');
    } finally {
      setLoading(false);
    }
  };

  // Create crew member
  const createCrew = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/frms/crew`, {
        method: 'POST',
        headers: apiHeaders,
        body: JSON.stringify(newCrew)
      });
      if (res.ok) {
        toast.success('Crew member created');
        setShowCrewDialog(false);
        setNewCrew({
          employee_id: '',
          name: '',
          name_ar: '',
          role: 'captain',
          license_number: '',
          home_base: 'OERK',
          home_timezone: 'Asia/Riyadh',
          aircraft_types: [],
          regulatory_framework: 'gaca',
        });
        await fetchCrew();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to create crew member');
      }
    } catch (err) {
      toast.error('Error creating crew member');
    } finally {
      setLoading(false);
    }
  };

  // Create duty period
  const createDuty = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/frms/duties`, {
        method: 'POST',
        headers: apiHeaders,
        body: JSON.stringify({
          ...newDuty,
          report_time: new Date(newDuty.report_time).toISOString(),
          release_time: newDuty.release_time ? new Date(newDuty.release_time).toISOString() : null,
        })
      });
      if (res.ok) {
        toast.success('Duty period created');
        setShowDutyDialog(false);
        setNewDuty({
          crew_member_id: '',
          duty_type: 'flight_duty',
          report_time: '',
          release_time: '',
          report_location: 'OERK',
          release_location: '',
          sectors: 1,
          total_flight_time: 0,
          augmented_crew: false,
          total_crew_count: 2,
        });
        await fetchDuties();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to create duty period');
      }
    } catch (err) {
      toast.error('Error creating duty period');
    } finally {
      setLoading(false);
    }
  };

  // Submit fatigue report
  const submitReport = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/frms/reports`, {
        method: 'POST',
        headers: apiHeaders,
        body: JSON.stringify(newReport)
      });
      if (res.ok) {
        toast.success('Fatigue report submitted');
        setShowReportDialog(false);
        setNewReport({
          crew_member_id: '',
          report_type: 'fatigue_event',
          title: '',
          description: '',
          event_date: new Date().toISOString().split('T')[0],
          hours_awake: null,
          hours_on_duty: null,
          sleep_in_last_24h: null,
          factors: [],
          safety_impact: 'none',
          anonymous: false,
        });
        await fetchReports();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to submit report');
      }
    } catch (err) {
      toast.error('Error submitting report');
    } finally {
      setLoading(false);
    }
  };

  // Acknowledge alert
  const acknowledgeAlert = async (alertId) => {
    try {
      const res = await fetch(`${API_URL}/api/frms/alerts/${alertId}/acknowledge`, {
        method: 'POST',
        headers: apiHeaders,
        body: JSON.stringify({ action_taken: 'Acknowledged by safety officer' })
      });
      if (res.ok) {
        toast.success('Alert acknowledged');
        await fetchAlerts();
      }
    } catch (err) {
      toast.error('Error acknowledging alert');
    }
  };

  // Fetch all data
  const fetchAll = async () => {
    setLoading(true);
    await Promise.all([
      fetchStats(),
      fetchCrew(),
      fetchDuties(),
      fetchAlerts(),
      fetchReports(),
      fetchLimits(),
      fetchReferenceData(),
      fetchRosters(),
    ]);
    setLoading(false);
  };

  useEffect(() => {
    fetchAll();
  }, [token]);

  // Format date/time
  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString();
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString();
  };

  // Get crew name by ID
  const getCrewName = (crewId) => {
    const member = crew.find(c => c.id === crewId);
    return member?.name || crewId;
  };

  // Risk level badge
  const RiskBadge = ({ level }) => {
    const config = riskLevelConfig[level] || riskLevelConfig.low;
    return (
      <Badge className={config.badge}>
        {config.label}
      </Badge>
    );
  };

  // Fatigue gauge component
  const FatigueGauge = ({ score, size = 'md' }) => {
    const sizeClasses = {
      sm: 'w-16 h-16',
      md: 'w-24 h-24',
      lg: 'w-32 h-32',
    };
    const getColor = (s) => {
      if (s >= 80) return 'text-rose-600';
      if (s >= 65) return 'text-red-500';
      if (s >= 50) return 'text-orange-500';
      if (s >= 35) return 'text-yellow-500';
      return 'text-green-500';
    };
    
    return (
      <div className={`${sizeClasses[size]} relative flex items-center justify-center`}>
        <div className={`absolute inset-0 rounded-full border-4 border-gray-200 dark:border-gray-700`} />
        <div 
          className={`absolute inset-0 rounded-full border-4 ${getColor(score)}`}
          style={{
            clipPath: `polygon(0 0, 100% 0, 100% ${score}%, 0 ${score}%)`,
            transform: 'rotate(-90deg)',
          }}
        />
        <span className={`${size === 'lg' ? 'text-2xl' : size === 'md' ? 'text-xl' : 'text-lg'} font-bold ${getColor(score)}`}>
          {Math.round(score)}
        </span>
      </div>
    );
  };

  return (
    <div className="p-6 space-y-6" data-testid="frms-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Moon className="h-7 w-7 text-indigo-600" />
            {t('frms.title')}
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {t('frms.subtitle')}
          </p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={fetchAll}
            disabled={loading}
            data-testid="refresh-btn"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button 
            onClick={seedDemoData}
            disabled={loading}
            data-testid="seed-data-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Seed Demo Data
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <Card data-testid="stat-total-crew">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">Total Crew</p>
                  <p className="text-2xl font-bold">{stats.total_crew}</p>
                </div>
                <Users className="h-8 w-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>

          <Card data-testid="stat-active-alerts">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">Active Alerts</p>
                  <p className="text-2xl font-bold text-orange-600">{stats.active_alerts}</p>
                </div>
                <AlertTriangle className="h-8 w-8 text-orange-500" />
              </div>
            </CardContent>
          </Card>

          <Card data-testid="stat-duties-24h">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">Duties (24h)</p>
                  <p className="text-2xl font-bold">{stats.duties_last_24h}</p>
                </div>
                <Clock className="h-8 w-8 text-purple-500" />
              </div>
            </CardContent>
          </Card>

          <Card data-testid="stat-violations">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">Violations (7d)</p>
                  <p className="text-2xl font-bold text-red-600">{stats.compliance_violations_7d}</p>
                </div>
                <Shield className="h-8 w-8 text-red-500" />
              </div>
            </CardContent>
          </Card>

          <Card data-testid="stat-avg-fatigue">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">Avg Fatigue</p>
                  <p className="text-2xl font-bold">{stats.average_fatigue_score || 0}</p>
                </div>
                <Gauge className="h-8 w-8 text-indigo-500" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Risk Distribution */}
      {stats && Object.keys(stats.risk_distribution || {}).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Crew Risk Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-4">
              {Object.entries(stats.risk_distribution).map(([level, count]) => (
                <div key={level} className="flex items-center gap-2">
                  <div className={`w-4 h-4 rounded-full ${riskLevelConfig[level]?.color || 'bg-gray-400'}`} />
                  <span className="text-sm font-medium capitalize">{level}:</span>
                  <span className="text-sm text-gray-600 dark:text-gray-400">{count} crew</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-7 w-full max-w-5xl">
          <TabsTrigger value="dashboard" data-testid="tab-dashboard">
            <Activity className="h-4 w-4 mr-2" />
            Dashboard
          </TabsTrigger>
          <TabsTrigger value="crew" data-testid="tab-crew">
            <Users className="h-4 w-4 mr-2" />
            Crew
          </TabsTrigger>
          <TabsTrigger value="duties" data-testid="tab-duties">
            <Clock className="h-4 w-4 mr-2" />
            Duties
          </TabsTrigger>
          <TabsTrigger value="scheduling" data-testid="tab-scheduling">
            <CalendarDays className="h-4 w-4 mr-2" />
            Scheduling
          </TabsTrigger>
          <TabsTrigger value="alerts" data-testid="tab-alerts">
            <AlertTriangle className="h-4 w-4 mr-2" />
            Alerts
          </TabsTrigger>
          <TabsTrigger value="reports" data-testid="tab-reports">
            <FileText className="h-4 w-4 mr-2" />
            Reports
          </TabsTrigger>
          <TabsTrigger value="limits" data-testid="tab-limits">
            <Settings className="h-4 w-4 mr-2" />
            Limits
          </TabsTrigger>
        </TabsList>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard" className="space-y-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* High Risk Crew */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <AlertCircle className="h-5 w-5 text-orange-500" />
                  High Risk Crew Members
                </CardTitle>
                <CardDescription>Crew requiring immediate attention</CardDescription>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-64">
                  {crew.filter(c => ['high', 'critical', 'unacceptable'].includes(c.current_risk_level)).length === 0 ? (
                    <p className="text-sm text-gray-500 text-center py-8">No high-risk crew members</p>
                  ) : (
                    <div className="space-y-3">
                      {crew.filter(c => ['high', 'critical', 'unacceptable'].includes(c.current_risk_level)).map(member => (
                        <div key={member.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                          <div className="flex items-center gap-3">
                            <div className={`w-3 h-3 rounded-full ${riskLevelConfig[member.current_risk_level]?.color}`} />
                            <div>
                              <p className="font-medium">{member.name}</p>
                              <p className="text-sm text-gray-500">{roleNames[member.role] || member.role}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-lg font-bold">{member.current_fatigue_score}</p>
                            <RiskBadge level={member.current_risk_level} />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>

            {/* Recent Alerts */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-red-500" />
                  Recent Alerts
                </CardTitle>
                <CardDescription>Unacknowledged fatigue alerts</CardDescription>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-64">
                  {alerts.filter(a => !a.acknowledged).length === 0 ? (
                    <p className="text-sm text-gray-500 text-center py-8">No unacknowledged alerts</p>
                  ) : (
                    <div className="space-y-3">
                      {alerts.filter(a => !a.acknowledged).slice(0, 5).map(alert => (
                        <div key={alert.id} className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                          <div className="flex items-start justify-between">
                            <div>
                              <p className="font-medium">{alert.title}</p>
                              <p className="text-sm text-gray-500">{getCrewName(alert.crew_member_id)}</p>
                              <p className="text-xs text-gray-400 mt-1">{formatDateTime(alert.created_at)}</p>
                            </div>
                            <RiskBadge level={alert.severity} />
                          </div>
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="mt-2"
                            onClick={() => acknowledgeAlert(alert.id)}
                          >
                            <CheckCircle2 className="h-4 w-4 mr-1" />
                            Acknowledge
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </CardContent>
            </Card>
          </div>

          {/* Samn-Perelli Scale Reference */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Samn-Perelli Fatigue Scale</CardTitle>
              <CardDescription>Self-assessment scale for crew fatigue reporting</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-7 gap-2">
                {samnPerelli.map(item => (
                  <div 
                    key={item.value} 
                    className={`p-3 rounded-lg text-center ${
                      item.value <= 2 ? 'bg-green-100 dark:bg-green-900/30' :
                      item.value <= 4 ? 'bg-yellow-100 dark:bg-yellow-900/30' :
                      item.value <= 5 ? 'bg-orange-100 dark:bg-orange-900/30' :
                      'bg-red-100 dark:bg-red-900/30'
                    }`}
                  >
                    <p className="text-2xl font-bold">{item.value}</p>
                    <p className="text-xs mt-1">{item.description}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Crew Tab */}
        <TabsContent value="crew" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold">Crew Members</h2>
            <Button onClick={() => setShowCrewDialog(true)} data-testid="add-crew-btn">
              <Plus className="h-4 w-4 mr-2" />
              Add Crew Member
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {crew.map(member => (
              <Card key={member.id} className="cursor-pointer hover:shadow-md transition-shadow" data-testid={`crew-card-${member.employee_id}`}>
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center ${riskLevelConfig[member.current_risk_level]?.color || 'bg-gray-400'}`}>
                        <User className="h-6 w-6 text-white" />
                      </div>
                      <div>
                        <p className="font-semibold">{member.name}</p>
                        {member.name_ar && <p className="text-sm text-gray-500" dir="rtl">{member.name_ar}</p>}
                        <p className="text-sm text-gray-500">{roleNames[member.role] || member.role}</p>
                      </div>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent>
                        <DropdownMenuItem onClick={() => { setSelectedCrew(member); setShowCrewDetail(true); }}>
                          <Eye className="h-4 w-4 mr-2" /> View Details
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => assessFatigue(member.id)}>
                          <Zap className="h-4 w-4 mr-2" /> Assess Fatigue
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>

                  <Separator className="my-4" />

                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-gray-500">Employee ID</p>
                      <p className="font-medium">{member.employee_id}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Home Base</p>
                      <p className="font-medium">{member.home_base}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Fatigue Score</p>
                      <p className="font-medium">{member.current_fatigue_score || 0}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Risk Level</p>
                      <RiskBadge level={member.current_risk_level || 'low'} />
                    </div>
                  </div>

                  <Separator className="my-4" />

                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Flight Time (7d)</span>
                      <span className="font-medium">{member.flight_time_7_days || 0}h</span>
                    </div>
                    <Progress value={(member.flight_time_7_days / 60) * 100 || 0} className="h-2" />
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Duty Time (7d)</span>
                      <span className="font-medium">{member.duty_time_7_days || 0}h</span>
                    </div>
                    <Progress value={(member.duty_time_7_days / 60) * 100 || 0} className="h-2" />
                  </div>

                  <div className="mt-4 flex flex-wrap gap-1">
                    {(member.aircraft_types || []).map(type => (
                      <Badge key={type} variant="outline" className="text-xs">
                        <Plane className="h-3 w-3 mr-1" />
                        {type}
                      </Badge>
                    ))}
                  </div>

                  <Badge className="mt-3" variant="secondary">
                    {frameworkNames[member.regulatory_framework] || member.regulatory_framework}
                  </Badge>
                </CardContent>
              </Card>
            ))}
          </div>

          {crew.length === 0 && (
            <Card>
              <CardContent className="py-12 text-center">
                <Users className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                <p className="text-gray-500">No crew members found.</p>
                <p className="text-sm text-gray-400 mt-1">Click "Seed Demo Data" to populate sample crew members.</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Duties Tab */}
        <TabsContent value="duties" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold">Duty Periods</h2>
            <Button onClick={() => setShowDutyDialog(true)} data-testid="add-duty-btn">
              <Plus className="h-4 w-4 mr-2" />
              Add Duty Period
            </Button>
          </div>

          <Card>
            <CardContent className="pt-6">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4">Crew Member</th>
                      <th className="text-left py-3 px-4">Duty Type</th>
                      <th className="text-left py-3 px-4">Report Time</th>
                      <th className="text-left py-3 px-4">Release Time</th>
                      <th className="text-left py-3 px-4">Location</th>
                      <th className="text-left py-3 px-4">Sectors</th>
                      <th className="text-left py-3 px-4">Flight Time</th>
                      <th className="text-left py-3 px-4">Status</th>
                      <th className="text-left py-3 px-4">Compliance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {duties.map(duty => (
                      <tr 
                        key={duty.id} 
                        className="border-b hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer"
                        onClick={() => setSelectedDuty(duty)}
                        data-testid={`duty-row-${duty.id}`}
                      >
                        <td className="py-3 px-4 font-medium">{getCrewName(duty.crew_member_id)}</td>
                        <td className="py-3 px-4">
                          <Badge variant="outline">{dutyTypeNames[duty.duty_type] || duty.duty_type}</Badge>
                        </td>
                        <td className="py-3 px-4">{formatDateTime(duty.report_time)}</td>
                        <td className="py-3 px-4">{formatDateTime(duty.release_time)}</td>
                        <td className="py-3 px-4">
                          <span className="flex items-center gap-1">
                            <MapPin className="h-3 w-3" />
                            {duty.report_location}
                            {duty.release_location && duty.release_location !== duty.report_location && (
                              <span className="text-gray-400">→ {duty.release_location}</span>
                            )}
                          </span>
                        </td>
                        <td className="py-3 px-4">{duty.sectors}</td>
                        <td className="py-3 px-4">{duty.total_flight_time || 0}h</td>
                        <td className="py-3 px-4">
                          <Badge variant={duty.status === 'completed' ? 'default' : 'secondary'}>
                            {duty.status}
                          </Badge>
                        </td>
                        <td className="py-3 px-4">
                          {duty.is_compliant ? (
                            <Badge className="bg-green-100 text-green-800">Compliant</Badge>
                          ) : (
                            <Badge className="bg-red-100 text-red-800">Violation</Badge>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {duties.length === 0 && (
                <div className="py-12 text-center">
                  <Clock className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-500">No duty periods found.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Scheduling Tab - Predictive Fatigue Scheduling */}
        <TabsContent value="scheduling" className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold">Predictive Fatigue Scheduling</h2>
              <p className="text-sm text-gray-500">Drag crew to time slots for real-time fatigue risk analysis</p>
            </div>
            <div className="flex gap-2">
              <div className="flex border rounded-lg overflow-hidden">
                <Button 
                  variant={schedulingView === 'drag-drop' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setSchedulingView('drag-drop')}
                  className="rounded-none"
                >
                  <GripVertical className="h-4 w-4 mr-1" />
                  Drag & Drop
                </Button>
                <Button 
                  variant={schedulingView === 'calendar' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setSchedulingView('calendar')}
                  className="rounded-none border-x"
                >
                  <Grid3X3 className="h-4 w-4 mr-1" />
                  Heatmap
                </Button>
                <Button 
                  variant={schedulingView === 'list' ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setSchedulingView('list')}
                  className="rounded-none"
                >
                  <List className="h-4 w-4 mr-1" />
                  List
                </Button>
              </div>
              <Button onClick={seedDemoRoster} disabled={loading} data-testid="create-demo-roster">
                <Plus className="h-4 w-4 mr-2" />
                Create Demo Roster
              </Button>
            </div>
          </div>

          {/* Roster Selection */}
          {rosters.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {rosters.map(roster => (
                <Button
                  key={roster.id}
                  variant={selectedRoster?.id === roster.id ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => selectRoster(roster)}
                  className="text-sm"
                >
                  {roster.name}
                  {roster.conflict_count > 0 && (
                    <Badge className="ml-2 bg-red-100 text-red-800">{roster.conflict_count}</Badge>
                  )}
                </Button>
              ))}
            </div>
          )}

          {selectedRoster && (
            <>
              {/* Roster Stats */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <Card>
                  <CardContent className="pt-4 pb-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-gray-500">Entries</p>
                        <p className="text-xl font-bold">{selectedRoster.entry_count}</p>
                      </div>
                      <CalendarDays className="h-6 w-6 text-blue-500" />
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 pb-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-gray-500">Conflicts</p>
                        <p className="text-xl font-bold text-red-600">{selectedRoster.conflict_count}</p>
                      </div>
                      <AlertTriangle className="h-6 w-6 text-red-500" />
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 pb-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-gray-500">High Risk</p>
                        <p className="text-xl font-bold text-orange-600">{selectedRoster.high_risk_count}</p>
                      </div>
                      <BatteryWarning className="h-6 w-6 text-orange-500" />
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 pb-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-gray-500">Status</p>
                        <Badge variant="outline" className="mt-1">{selectedRoster.status}</Badge>
                      </div>
                      <CheckCircle2 className="h-6 w-6 text-green-500" />
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 pb-4">
                    <Button 
                      onClick={() => analyzeRoster(selectedRoster.id)} 
                      disabled={loading}
                      className="w-full"
                      data-testid="analyze-roster-btn"
                    >
                      <Zap className="h-4 w-4 mr-2" />
                      Re-Analyze
                    </Button>
                  </CardContent>
                </Card>
              </div>

              {/* Drag & Drop View */}
              {schedulingView === 'drag-drop' && (
                <DragDropScheduler
                  roster={selectedRoster}
                  entries={rosterEntries}
                  crew={crew}
                  heatmap={rosterHeatmap}
                  conflicts={rosterConflicts}
                  onAssign={async (assignment) => {
                    // TODO: Implement roster entry creation API
                    toast.info('Assignment saved - re-analyzing roster...');
                    await analyzeRoster(selectedRoster.id);
                  }}
                  onRemove={async (entryId) => {
                    // TODO: Implement roster entry deletion API
                    toast.info('Entry removed');
                    await selectRoster(selectedRoster);
                  }}
                  onAnalyze={() => analyzeRoster(selectedRoster.id)}
                  loading={loading}
                />
              )}

              {/* Calendar Heatmap View */}
              {schedulingView === 'calendar' && rosterHeatmap.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Calendar className="h-5 w-5 text-indigo-500" />
                      Risk Heatmap
                    </CardTitle>
                    <CardDescription>Daily fatigue risk levels across the roster period</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-7 gap-2">
                      {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                        <div key={day} className="text-center text-xs font-medium text-gray-500 pb-2">
                          {day}
                        </div>
                      ))}
                      {rosterHeatmap.map((day, idx) => {
                        const riskColor = {
                          low: 'bg-green-100 hover:bg-green-200 border-green-300',
                          moderate: 'bg-yellow-100 hover:bg-yellow-200 border-yellow-300',
                          high: 'bg-orange-100 hover:bg-orange-200 border-orange-300',
                          critical: 'bg-red-100 hover:bg-red-200 border-red-300',
                          unacceptable: 'bg-rose-200 hover:bg-rose-300 border-rose-400',
                        };
                        return (
                          <div
                            key={day.date}
                            className={`p-3 rounded-lg border cursor-pointer transition-colors ${riskColor[day.max_risk_level] || 'bg-gray-50 border-gray-200'}`}
                            title={`${day.date}: ${day.entry_count} entries, ${day.conflict_count} conflicts`}
                          >
                            <div className="text-xs font-medium">
                              {new Date(day.date + 'T00:00:00').getDate()}
                            </div>
                            <div className="text-lg font-bold mt-1">
                              {Math.round(day.average_fatigue_score)}
                            </div>
                            <div className="flex items-center gap-1 mt-1">
                              <span className="text-xs text-gray-600">{day.entry_count}</span>
                              {day.conflict_count > 0 && (
                                <AlertTriangle className="h-3 w-3 text-red-500" />
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    {/* Legend */}
                    <div className="flex flex-wrap gap-4 mt-4 pt-4 border-t">
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 rounded bg-green-100 border border-green-300" />
                        <span className="text-xs">Low Risk</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 rounded bg-yellow-100 border border-yellow-300" />
                        <span className="text-xs">Moderate</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 rounded bg-orange-100 border border-orange-300" />
                        <span className="text-xs">High</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 rounded bg-red-100 border border-red-300" />
                        <span className="text-xs">Critical</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 rounded bg-rose-200 border border-rose-400" />
                        <span className="text-xs">Unacceptable</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* List View */}
              {schedulingView === 'list' && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Roster Entries</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left py-3 px-4">Date</th>
                            <th className="text-left py-3 px-4">Crew</th>
                            <th className="text-left py-3 px-4">Duty Type</th>
                            <th className="text-left py-3 px-4">Route</th>
                            <th className="text-left py-3 px-4">Fatigue Score</th>
                            <th className="text-left py-3 px-4">Risk Level</th>
                            <th className="text-left py-3 px-4">Conflicts</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rosterEntries.map(entry => (
                            <tr 
                              key={entry.id} 
                              className={`border-b hover:bg-gray-50 dark:hover:bg-gray-800 ${entry.has_conflicts ? 'bg-red-50 dark:bg-red-900/10' : ''}`}
                            >
                              <td className="py-3 px-4">{entry.duty_date}</td>
                              <td className="py-3 px-4 font-medium">{entry.crew_member_name || getCrewName(entry.crew_member_id)}</td>
                              <td className="py-3 px-4">
                                <Badge variant="outline">{dutyTypeNames[entry.duty_type] || entry.duty_type}</Badge>
                              </td>
                              <td className="py-3 px-4">
                                <span className="flex items-center gap-1">
                                  {entry.report_location}
                                  <ArrowRight className="h-3 w-3" />
                                  {entry.release_location || entry.report_location}
                                </span>
                              </td>
                              <td className="py-3 px-4">
                                <span className={`font-bold ${
                                  entry.predicted_fatigue_score >= 65 ? 'text-red-600' :
                                  entry.predicted_fatigue_score >= 50 ? 'text-orange-600' :
                                  entry.predicted_fatigue_score >= 35 ? 'text-yellow-600' :
                                  'text-green-600'
                                }`}>
                                  {entry.predicted_fatigue_score || '-'}
                                </span>
                              </td>
                              <td className="py-3 px-4">
                                <RiskBadge level={entry.predicted_risk_level || 'low'} />
                              </td>
                              <td className="py-3 px-4">
                                {entry.has_conflicts ? (
                                  <Badge className="bg-red-100 text-red-800">
                                    {entry.conflicts?.length || 1} conflict(s)
                                  </Badge>
                                ) : (
                                  <Badge className="bg-green-100 text-green-800">OK</Badge>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Conflicts List */}
              {rosterConflicts.length > 0 && (
                <Card className="border-red-500">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2 text-red-600">
                      <AlertTriangle className="h-5 w-5" />
                      Scheduling Conflicts ({rosterConflicts.length})
                    </CardTitle>
                    <CardDescription>Issues that need to be resolved before roster publication</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {rosterConflicts.slice(0, 10).map(conflict => (
                        <div key={conflict.id} className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                          <div className="flex items-start justify-between">
                            <div>
                              <div className="flex items-center gap-2">
                                <Badge className={severityConfig[conflict.severity]?.badge || 'bg-gray-100'}>
                                  {conflict.severity}
                                </Badge>
                                <span className="font-medium capitalize">{conflict.conflict_type.replace('_', ' ')}</span>
                              </div>
                              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{conflict.description}</p>
                              <p className="text-xs text-gray-500 mt-2">
                                Limit: {conflict.limit_value} | Actual: {conflict.actual_value.toFixed(1)} | Overage: {conflict.overage.toFixed(1)}
                              </p>
                            </div>
                          </div>
                          {conflict.resolution_options?.length > 0 && (
                            <div className="mt-2 pt-2 border-t border-red-200 dark:border-red-800">
                              <p className="text-xs font-medium text-gray-500 mb-1">Resolution Options:</p>
                              <div className="flex flex-wrap gap-1">
                                {conflict.resolution_options.map((opt, idx) => (
                                  <Badge key={idx} variant="outline" className="text-xs">
                                    {opt}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          )}

          {rosters.length === 0 && (
            <Card>
              <CardContent className="py-12 text-center">
                <CalendarDays className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                <p className="text-gray-500">No rosters found.</p>
                <p className="text-sm text-gray-400 mt-1">Click "Create Demo Roster" to generate a sample roster with fatigue analysis.</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Alerts Tab */}
        <TabsContent value="alerts" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold">Fatigue Alerts</h2>
            <Badge variant="outline">{alerts.length} total</Badge>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {alerts.map(alert => (
              <Card key={alert.id} className={`${!alert.acknowledged ? 'border-orange-500 border-2' : ''}`} data-testid={`alert-card-${alert.id}`}>
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <AlertTriangle className={`h-6 w-6 ${
                        alert.severity === 'critical' || alert.severity === 'unacceptable' ? 'text-red-500' :
                        alert.severity === 'high' ? 'text-orange-500' : 'text-yellow-500'
                      }`} />
                      <div>
                        <p className="font-semibold">{alert.title}</p>
                        <p className="text-sm text-gray-500">{getCrewName(alert.crew_member_id)}</p>
                      </div>
                    </div>
                    <RiskBadge level={alert.severity} />
                  </div>

                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-3">{alert.message}</p>

                  <div className="mt-4 flex items-center justify-between">
                    <span className="text-xs text-gray-400">{formatDateTime(alert.created_at)}</span>
                    {!alert.acknowledged ? (
                      <Button size="sm" onClick={() => acknowledgeAlert(alert.id)}>
                        <CheckCircle2 className="h-4 w-4 mr-1" />
                        Acknowledge
                      </Button>
                    ) : (
                      <Badge variant="secondary">Acknowledged</Badge>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {alerts.length === 0 && (
            <Card>
              <CardContent className="py-12 text-center">
                <CheckCircle2 className="h-12 w-12 mx-auto text-green-500 mb-4" />
                <p className="text-gray-500">No active alerts. All crew fatigue levels are within acceptable limits.</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Reports Tab */}
        <TabsContent value="reports" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold">Fatigue Reports</h2>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => { setShowReportDialog(true); setVoiceOpen(true); }}
                className="border-rose-500/50 text-rose-500 hover:bg-rose-500/10"
                data-testid="voice-file-fatigue-btn"
                title="Speak the fatigue report — the AI will fill in the form"
              >
                <Mic className="h-4 w-4 mr-2" />
                File by Voice
              </Button>
              <Button onClick={() => setShowReportDialog(true)} data-testid="submit-report-btn">
                <Plus className="h-4 w-4 mr-2" />
                Submit Report
              </Button>
            </div>
          </div>

          <VoiceIncidentRecorder
            open={voiceOpen}
            mode="fatigue"
            onClose={() => setVoiceOpen(false)}
            onApply={(ex) => {
              if (!ex) return;
              setNewReport((prev) => ({
                ...prev,
                report_type: ex.report_type || prev.report_type,
                title: ex.title || prev.title,
                description: ex.description || prev.description,
                event_date: ex.event_date || prev.event_date,
                hours_awake: ex.hours_awake ?? prev.hours_awake,
                hours_on_duty: ex.hours_on_duty ?? prev.hours_on_duty,
                sleep_in_last_24h: ex.sleep_in_last_24h ?? prev.sleep_in_last_24h,
                factors: (ex.factors && ex.factors.length ? ex.factors : prev.factors) || [],
                safety_impact: ex.safety_impact || prev.safety_impact,
                anonymous: typeof ex.anonymous === 'boolean' ? ex.anonymous : prev.anonymous,
              }));
              setVoiceOpen(false);
              toast.success('Voice report applied to form');
            }}
          />

          <Card>
            <CardContent className="pt-6">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4">Date</th>
                      <th className="text-left py-3 px-4">Crew Member</th>
                      <th className="text-left py-3 px-4">Type</th>
                      <th className="text-left py-3 px-4">Title</th>
                      <th className="text-left py-3 px-4">Safety Impact</th>
                      <th className="text-left py-3 px-4">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reports.map(report => (
                      <tr key={report.id} className="border-b hover:bg-gray-50 dark:hover:bg-gray-800">
                        <td className="py-3 px-4">{formatDate(report.event_date)}</td>
                        <td className="py-3 px-4">
                          {report.anonymous ? <span className="italic text-gray-500">Anonymous</span> : getCrewName(report.crew_member_id)}
                        </td>
                        <td className="py-3 px-4">
                          <Badge variant="outline">{report.report_type.replace('_', ' ')}</Badge>
                        </td>
                        <td className="py-3 px-4 font-medium">{report.title}</td>
                        <td className="py-3 px-4">
                          <Badge className={
                            report.safety_impact === 'serious' ? 'bg-red-100 text-red-800' :
                            report.safety_impact === 'moderate' ? 'bg-orange-100 text-orange-800' :
                            report.safety_impact === 'minor' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-gray-100 text-gray-800'
                          }>
                            {report.safety_impact}
                          </Badge>
                        </td>
                        <td className="py-3 px-4">
                          <Badge variant={report.status === 'closed' ? 'default' : 'secondary'}>
                            {report.status}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {reports.length === 0 && (
                <div className="py-12 text-center">
                  <FileText className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-500">No fatigue reports submitted.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Limits Tab */}
        <TabsContent value="limits" className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-semibold">Regulatory Limits</h2>
            <Badge variant="outline">{limits.length} frameworks</Badge>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {limits.map(limit => (
              <Card key={limit.id || limit.framework} data-testid={`limits-card-${limit.framework}`}>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="h-5 w-5 text-indigo-500" />
                    {limit.name}
                  </CardTitle>
                  <CardDescription>{limit.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-gray-500 mb-2 font-semibold">Flight Duty Period (FDP)</p>
                      <div className="space-y-1">
                        <div className="flex justify-between">
                          <span>1-2 Sectors:</span>
                          <span className="font-medium">{limit.max_fdp_2_sectors}h</span>
                        </div>
                        <div className="flex justify-between">
                          <span>3 Sectors:</span>
                          <span className="font-medium">{limit.max_fdp_3_sectors}h</span>
                        </div>
                        <div className="flex justify-between">
                          <span>4 Sectors:</span>
                          <span className="font-medium">{limit.max_fdp_4_sectors}h</span>
                        </div>
                        <div className="flex justify-between">
                          <span>5 Sectors:</span>
                          <span className="font-medium">{limit.max_fdp_5_sectors}h</span>
                        </div>
                        <div className="flex justify-between">
                          <span>6+ Sectors:</span>
                          <span className="font-medium">{limit.max_fdp_6_plus_sectors}h</span>
                        </div>
                      </div>
                    </div>

                    <div>
                      <p className="text-gray-500 mb-2 font-semibold">Rest Requirements</p>
                      <div className="space-y-1">
                        <div className="flex justify-between">
                          <span>Min Rest:</span>
                          <span className="font-medium">{limit.min_rest_period}h</span>
                        </div>
                        <div className="flex justify-between">
                          <span>After Extension:</span>
                          <span className="font-medium">{limit.min_rest_after_fdp_extension}h</span>
                        </div>
                        <div className="flex justify-between">
                          <span>After WOCL:</span>
                          <span className="font-medium">{limit.min_rest_after_wocl}h</span>
                        </div>
                      </div>
                    </div>

                    <div>
                      <p className="text-gray-500 mb-2 font-semibold">Flight Time Limits</p>
                      <div className="space-y-1">
                        <div className="flex justify-between">
                          <span>Daily:</span>
                          <span className="font-medium">{limit.max_flight_time_daily}h</span>
                        </div>
                        <div className="flex justify-between">
                          <span>7 Days:</span>
                          <span className="font-medium">{limit.max_flight_time_7_days}h</span>
                        </div>
                        <div className="flex justify-between">
                          <span>28 Days:</span>
                          <span className="font-medium">{limit.max_flight_time_28_days}h</span>
                        </div>
                        <div className="flex justify-between">
                          <span>365 Days:</span>
                          <span className="font-medium">{limit.max_flight_time_365_days}h</span>
                        </div>
                      </div>
                    </div>

                    <div>
                      <p className="text-gray-500 mb-2 font-semibold">WOCL</p>
                      <div className="space-y-1">
                        <div className="flex justify-between">
                          <span>Window:</span>
                          <span className="font-medium">{limit.wocl_start_hour}:00 - {limit.wocl_end_hour}:00</span>
                        </div>
                        <div className="flex justify-between">
                          <span>FDP Reduction:</span>
                          <span className="font-medium">{limit.fdp_reduction_wocl_encroachment}h</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Max Night Duties:</span>
                          <span className="font-medium">{limit.max_consecutive_night_duties}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {limits.length === 0 && (
            <Card>
              <CardContent className="py-12 text-center">
                <Settings className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                <p className="text-gray-500">No regulatory limits configured.</p>
                <p className="text-sm text-gray-400 mt-1">Click "Seed Demo Data" to populate default limits.</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Add Crew Member Dialog */}
      <Dialog open={showCrewDialog} onOpenChange={setShowCrewDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Crew Member</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Employee ID *</Label>
                <Input 
                  value={newCrew.employee_id}
                  onChange={(e) => setNewCrew({ ...newCrew, employee_id: e.target.value })}
                  placeholder="CPT001"
                  data-testid="crew-employee-id"
                />
              </div>
              <div>
                <Label>License Number</Label>
                <Input 
                  value={newCrew.license_number}
                  onChange={(e) => setNewCrew({ ...newCrew, license_number: e.target.value })}
                  placeholder="GACA-ATP-12345"
                />
              </div>
            </div>

            <div>
              <Label>Name (English) *</Label>
              <Input 
                value={newCrew.name}
                onChange={(e) => setNewCrew({ ...newCrew, name: e.target.value })}
                placeholder="Captain Ahmed Al-Rashid"
                data-testid="crew-name"
              />
            </div>

            <div>
              <Label>Name (Arabic)</Label>
              <Input 
                value={newCrew.name_ar}
                onChange={(e) => setNewCrew({ ...newCrew, name_ar: e.target.value })}
                placeholder="الكابتن أحمد الراشد"
                dir="rtl"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Role *</Label>
                <Select value={newCrew.role} onValueChange={(v) => setNewCrew({ ...newCrew, role: v })}>
                  <SelectTrigger data-testid="crew-role">
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    {crewRoles.map(role => (
                      <SelectItem key={role.id} value={role.id}>{role.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Regulatory Framework</Label>
                <Select value={newCrew.regulatory_framework} onValueChange={(v) => setNewCrew({ ...newCrew, regulatory_framework: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select framework" />
                  </SelectTrigger>
                  <SelectContent>
                    {frameworks.map(fw => (
                      <SelectItem key={fw.id} value={fw.id}>{fw.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Home Base (ICAO) *</Label>
                <Input 
                  value={newCrew.home_base}
                  onChange={(e) => setNewCrew({ ...newCrew, home_base: e.target.value.toUpperCase() })}
                  placeholder="OERK"
                  maxLength={4}
                />
              </div>
              <div>
                <Label>Home Timezone</Label>
                <Input 
                  value={newCrew.home_timezone}
                  onChange={(e) => setNewCrew({ ...newCrew, home_timezone: e.target.value })}
                  placeholder="Asia/Riyadh"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCrewDialog(false)}>Cancel</Button>
            <Button onClick={createCrew} disabled={loading || !newCrew.employee_id || !newCrew.name}>
              Create Crew Member
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Crew Detail Dialog */}
      <Dialog open={showCrewDetail} onOpenChange={setShowCrewDetail}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Crew Member Details</DialogTitle>
          </DialogHeader>
          {selectedCrew && (
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <div className={`w-16 h-16 rounded-full flex items-center justify-center ${riskLevelConfig[selectedCrew.current_risk_level]?.color || 'bg-gray-400'}`}>
                  <User className="h-8 w-8 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold">{selectedCrew.name}</h3>
                  {selectedCrew.name_ar && <p className="text-gray-500" dir="rtl">{selectedCrew.name_ar}</p>}
                  <div className="flex gap-2 mt-2">
                    <Badge>{roleNames[selectedCrew.role] || selectedCrew.role}</Badge>
                    <RiskBadge level={selectedCrew.current_risk_level || 'low'} />
                  </div>
                </div>
              </div>

              <Separator />

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Employee ID</p>
                  <p className="font-medium">{selectedCrew.employee_id}</p>
                </div>
                <div>
                  <p className="text-gray-500">License Number</p>
                  <p className="font-medium">{selectedCrew.license_number || '-'}</p>
                </div>
                <div>
                  <p className="text-gray-500">Home Base</p>
                  <p className="font-medium">{selectedCrew.home_base}</p>
                </div>
                <div>
                  <p className="text-gray-500">Timezone</p>
                  <p className="font-medium">{selectedCrew.home_timezone}</p>
                </div>
                <div>
                  <p className="text-gray-500">Regulatory Framework</p>
                  <p className="font-medium">{frameworkNames[selectedCrew.regulatory_framework] || selectedCrew.regulatory_framework}</p>
                </div>
                <div>
                  <p className="text-gray-500">Current Fatigue Score</p>
                  <p className="font-medium text-xl">{selectedCrew.current_fatigue_score || 0}</p>
                </div>
              </div>

              <Separator />

              <div>
                <h4 className="font-semibold mb-3">Cumulative Flight/Duty Time</h4>
                <div className="space-y-3">
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span>Flight Time (7 days)</span>
                      <span>{selectedCrew.flight_time_7_days || 0}h / 60h</span>
                    </div>
                    <Progress value={(selectedCrew.flight_time_7_days / 60) * 100 || 0} />
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span>Flight Time (28 days)</span>
                      <span>{selectedCrew.flight_time_28_days || 0}h / 190h</span>
                    </div>
                    <Progress value={(selectedCrew.flight_time_28_days / 190) * 100 || 0} />
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span>Duty Time (7 days)</span>
                      <span>{selectedCrew.duty_time_7_days || 0}h / 60h</span>
                    </div>
                    <Progress value={(selectedCrew.duty_time_7_days / 60) * 100 || 0} />
                  </div>
                </div>
              </div>

              <div>
                <h4 className="font-semibold mb-3">Aircraft Types</h4>
                <div className="flex flex-wrap gap-2">
                  {(selectedCrew.aircraft_types || []).map(type => (
                    <Badge key={type} variant="outline">
                      <Plane className="h-3 w-3 mr-1" />
                      {type}
                    </Badge>
                  ))}
                  {(selectedCrew.aircraft_types || []).length === 0 && (
                    <span className="text-gray-500 text-sm">No aircraft types assigned</span>
                  )}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCrewDetail(false)}>Close</Button>
            <Button onClick={() => { assessFatigue(selectedCrew.id); setShowCrewDetail(false); }}>
              <Zap className="h-4 w-4 mr-2" />
              Assess Fatigue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Duty Period Dialog */}
      <Dialog open={showDutyDialog} onOpenChange={setShowDutyDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Duty Period</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Crew Member *</Label>
              <Select value={newDuty.crew_member_id} onValueChange={(v) => setNewDuty({ ...newDuty, crew_member_id: v })}>
                <SelectTrigger data-testid="duty-crew-select">
                  <SelectValue placeholder="Select crew member" />
                </SelectTrigger>
                <SelectContent>
                  {crew.map(member => (
                    <SelectItem key={member.id} value={member.id}>{member.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Duty Type *</Label>
              <Select value={newDuty.duty_type} onValueChange={(v) => setNewDuty({ ...newDuty, duty_type: v })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select duty type" />
                </SelectTrigger>
                <SelectContent>
                  {dutyTypes.map(type => (
                    <SelectItem key={type.id} value={type.id}>{type.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Report Time *</Label>
                <Input 
                  type="datetime-local"
                  value={newDuty.report_time}
                  onChange={(e) => setNewDuty({ ...newDuty, report_time: e.target.value })}
                  data-testid="duty-report-time"
                />
              </div>
              <div>
                <Label>Release Time</Label>
                <Input 
                  type="datetime-local"
                  value={newDuty.release_time}
                  onChange={(e) => setNewDuty({ ...newDuty, release_time: e.target.value })}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Report Location (ICAO) *</Label>
                <Input 
                  value={newDuty.report_location}
                  onChange={(e) => setNewDuty({ ...newDuty, report_location: e.target.value.toUpperCase() })}
                  placeholder="OERK"
                  maxLength={4}
                />
              </div>
              <div>
                <Label>Release Location (ICAO)</Label>
                <Input 
                  value={newDuty.release_location}
                  onChange={(e) => setNewDuty({ ...newDuty, release_location: e.target.value.toUpperCase() })}
                  placeholder="OEJN"
                  maxLength={4}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Sectors</Label>
                <Input 
                  type="number"
                  min="0"
                  value={newDuty.sectors}
                  onChange={(e) => setNewDuty({ ...newDuty, sectors: parseInt(e.target.value) || 0 })}
                />
              </div>
              <div>
                <Label>Flight Time (hours)</Label>
                <Input 
                  type="number"
                  min="0"
                  step="0.5"
                  value={newDuty.total_flight_time}
                  onChange={(e) => setNewDuty({ ...newDuty, total_flight_time: parseFloat(e.target.value) || 0 })}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDutyDialog(false)}>Cancel</Button>
            <Button onClick={createDuty} disabled={loading || !newDuty.crew_member_id || !newDuty.report_time || !newDuty.report_location}>
              Create Duty Period
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Submit Fatigue Report Dialog */}
      <Dialog open={showReportDialog} onOpenChange={setShowReportDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Submit Fatigue Report</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Crew Member *</Label>
              <Select value={newReport.crew_member_id} onValueChange={(v) => setNewReport({ ...newReport, crew_member_id: v })}>
                <SelectTrigger data-testid="report-crew-select">
                  <SelectValue placeholder="Select crew member" />
                </SelectTrigger>
                <SelectContent>
                  {crew.map(member => (
                    <SelectItem key={member.id} value={member.id}>{member.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Title *</Label>
              <Input 
                value={newReport.title}
                onChange={(e) => setNewReport({ ...newReport, title: e.target.value })}
                placeholder="Fatigue event during night duty"
                data-testid="report-title"
              />
            </div>

            <div>
              <div className="flex items-center justify-between">
                <Label>Description *</Label>
                <PolishButton
                  text={newReport.description}
                  kind="fatigue"
                  token={token}
                  onApply={(polished) => setNewReport((prev) => ({ ...prev, description: polished }))}
                />
              </div>
              <Textarea 
                value={newReport.description}
                onChange={(e) => setNewReport({ ...newReport, description: e.target.value })}
                placeholder="Describe the fatigue event and circumstances..."
                rows={4}
              />
              <AIFieldSuggestions
                kind="fatigue"
                title={newReport.title}
                description={newReport.description}
                token={token}
                onApply={(patch) => setNewReport((prev) => ({
                  ...prev,
                  ...(patch.report_type && { report_type: patch.report_type }),
                  ...(patch.hours_awake != null && { hours_awake: patch.hours_awake }),
                  ...(patch.hours_on_duty != null && { hours_on_duty: patch.hours_on_duty }),
                  ...(patch.sleep_in_last_24h != null && { sleep_in_last_24h: patch.sleep_in_last_24h }),
                  ...(patch.safety_impact && { safety_impact: patch.safety_impact }),
                  ...(patch.contributing_factors && { factors: patch.contributing_factors }),
                }))}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Event Date *</Label>
                <Input 
                  type="date"
                  value={newReport.event_date}
                  onChange={(e) => setNewReport({ ...newReport, event_date: e.target.value })}
                />
              </div>
              <div>
                <Label>Safety Impact</Label>
                <Select value={newReport.safety_impact} onValueChange={(v) => setNewReport({ ...newReport, safety_impact: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select impact" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    <SelectItem value="minor">Minor</SelectItem>
                    <SelectItem value="moderate">Moderate</SelectItem>
                    <SelectItem value="serious">Serious</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Hours Awake</Label>
                <Input 
                  type="number"
                  min="0"
                  value={newReport.hours_awake || ''}
                  onChange={(e) => setNewReport({ ...newReport, hours_awake: parseFloat(e.target.value) || null })}
                />
              </div>
              <div>
                <Label>Hours on Duty</Label>
                <Input 
                  type="number"
                  min="0"
                  value={newReport.hours_on_duty || ''}
                  onChange={(e) => setNewReport({ ...newReport, hours_on_duty: parseFloat(e.target.value) || null })}
                />
              </div>
              <div>
                <Label>Sleep (24h)</Label>
                <Input 
                  type="number"
                  min="0"
                  max="24"
                  value={newReport.sleep_in_last_24h || ''}
                  onChange={(e) => setNewReport({ ...newReport, sleep_in_last_24h: parseFloat(e.target.value) || null })}
                />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <input 
                type="checkbox"
                id="anonymous"
                checked={newReport.anonymous}
                onChange={(e) => setNewReport({ ...newReport, anonymous: e.target.checked })}
              />
              <Label htmlFor="anonymous">Submit anonymously</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReportDialog(false)}>Cancel</Button>
            <Button onClick={submitReport} disabled={loading || !newReport.crew_member_id || !newReport.title || !newReport.description}>
              Submit Report
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Fatigue Assessment Result Dialog */}
      <Dialog open={showFatigueAssessment} onOpenChange={setShowFatigueAssessment}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Fatigue Assessment Result</DialogTitle>
          </DialogHeader>
          {assessmentResult && (
            <div className="space-y-6">
              <div className="flex items-center justify-center gap-8">
                <FatigueGauge score={assessmentResult.fatigue_score} size="lg" />
                <div className="text-center">
                  <p className="text-sm text-gray-500">Risk Level</p>
                  <RiskBadge level={assessmentResult.risk_level} />
                </div>
              </div>

              <Separator />

              <div>
                <h4 className="font-semibold mb-3">Component Scores</h4>
                <div className="space-y-3">
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span>Sleep Debt</span>
                      <span>{assessmentResult.sleep_debt_score}</span>
                    </div>
                    <Progress value={assessmentResult.sleep_debt_score} className="h-2" />
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span>Circadian</span>
                      <span>{assessmentResult.circadian_score}</span>
                    </div>
                    <Progress value={assessmentResult.circadian_score} className="h-2" />
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span>Time Awake</span>
                      <span>{assessmentResult.time_awake_score}</span>
                    </div>
                    <Progress value={assessmentResult.time_awake_score} className="h-2" />
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span>Workload</span>
                      <span>{assessmentResult.workload_score}</span>
                    </div>
                    <Progress value={assessmentResult.workload_score} className="h-2" />
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span>Cumulative</span>
                      <span>{assessmentResult.cumulative_score}</span>
                    </div>
                    <Progress value={assessmentResult.cumulative_score} className="h-2" />
                  </div>
                </div>
              </div>

              <Separator />

              {assessmentResult.recommendations && assessmentResult.recommendations.length > 0 && (
                <div>
                  <h4 className="font-semibold mb-3">Recommendations</h4>
                  <ul className="space-y-2">
                    {assessmentResult.recommendations.map((rec, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm">
                        <ChevronRight className="h-4 w-4 text-indigo-500 mt-0.5 flex-shrink-0" />
                        <span>{rec}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setShowFatigueAssessment(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
